import React, { useEffect, useLayoutEffect, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../ui';

/**
 * Drawer
 *
 * Panel lateral derecho para detalle de entidad — patrón decidido en el
 * proyecto (INSTRUCCIONES: detalles de entidad → Drawer, no modal).
 *
 *   - Ancho 580px (máx 90vw)
 *   - Desliza desde la derecha
 *   - Overlay oscuro al 25%
 *   - ESC cierra · click fuera cierra
 *   - Permite ver el listado de fondo
 *
 * Componente propio del módulo (el DS v2 no trae Drawer, solo Dialog).
 * Reutilizable: III.6 lo amplía con timeline + acciones.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   title?: ReactNode
 *   subtitle?: ReactNode
 *   footer?: ReactNode
 *   bare?: boolean   — si true, omite el header/footer sobrios y deja
 *                       que `children` controle todo el panel (header
 *                       rico, tabs, footer propios). Retrocompatible:
 *                       sin esta prop el comportamiento es idéntico.
 *   children
 */

// Fallback (px) si no se localiza el navbar en el DOM: alto real aproximado
// del navbar global (`px-2 py-1` sobre su contenido), NO el 64 que causaba el
// hueco. En condiciones normales se sustituye por la medición dinámica de abajo.
const NAVBAR_FALLBACK = 48;

export default function Drawer({ open, onClose, title, subtitle, footer, bare = false, children }) {
  // Modo `bare`: el drawer arranca justo bajo el navbar (sin hueco superior) y
  // llega al fondo (bottom:0). Medimos el navbar REAL en vez de una constante
  // hardcodeada — así el drawer sigue alineado aunque el navbar cambie de alto.
  const [navbarHeight, setNavbarHeight] = useState(NAVBAR_FALLBACK);
  useLayoutEffect(() => {
    if (!open || !bare) return undefined;
    const nav = document.querySelector('[data-testid="app-navbar"]');
    if (!nav) return undefined; // no encontrado → se mantiene NAVBAR_FALLBACK
    const medir = () => setNavbarHeight(nav.getBoundingClientRect().height);
    medir();
    const ro = new ResizeObserver(medir); // re-alinea si el navbar cambia de alto
    ro.observe(nav);
    return () => ro.disconnect();
  }, [open, bare]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    // Bloquear scroll del body mientras el drawer está abierto
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/25"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'absolute right-0 bg-white shadow-xl',
          'w-[580px] max-w-[90vw] flex flex-col',
          // En modo bare arranca bajo el navbar y redondea la esquina
          // izquierda para que el header oscuro se lea como panel
          // intencionado. Non-bare se mantiene idéntico (retrocompat).
          bare ? 'rounded-l-xl overflow-hidden' : 'top-0 h-full',
          'animate-[drawerIn_180ms_ease-out]'
        )}
        style={{
          animationName: 'drawerIn',
          ...(bare ? { top: navbarHeight, bottom: 0 } : null),
        }}
      >
        <style>{`@keyframes drawerIn{from{transform:translateX(24px);opacity:.6}to{transform:translateX(0);opacity:1}}`}</style>

        {bare ? (
          // El consumidor controla todo el panel (header rico, tabs,
          // scroll y footer propios). Ocupa el alto completo.
          <div className="flex-1 min-h-0 flex flex-col">{children}</div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
              <div className="min-w-0">
                {title && (
                  <h2 className="text-base font-semibold text-slate-900 truncate">
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-700 shrink-0 p-1 -m-1"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

            {/* Footer */}
            {footer && (
              <div className="border-t border-slate-100 px-5 py-3 bg-slate-50">
                {footer}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
